/**
 * LegAnalysisCards
 * Renders leg analysis cards: a list of analyzed legs or a single leg fallback.
 */

import type { LegAnalysisCardData, ScoredLeg } from '@parlay-picker/shared/chatTypes';
import { leagueKeyOrNull } from '@parlay-picker/shared/league';
import { formatStatLabel } from '@parlay-picker/shared/statLabels';
import { findTeam } from '@parlay-picker/shared/teams';
import { usePlayerHeadshotUrl } from '@views/picks/utils';
import type { ReactElement } from 'react';
import { AvatarWithBadge, VerdictBadge } from './cardHelpers';

export function LegAnalysisCard({ data }: { data: LegAnalysisCardData }): ReactElement {
  const legs = data.legs;
  if (legs && legs.length > 0) {
    return (
      <div className="space-y-1.5">
        {legs.map((leg, i) => (
          <AnalysisLegRow key={i} leg={leg} />
        ))}
      </div>
    );
  }
  return <div className="text-xs text-theme-text-secondary">No leg analysis data</div>;
}

export function AnalysisLegRow({ leg }: { leg: ScoredLeg }): ReactElement {
  const player = leg.playerName || 'Unknown';
  const teamAbbr = leg.teamAbbr || '';
  const opponentAbbr = leg.opponentAbbr || '';
  const prop = leg.statType || '';
  const line = leg.line != null ? String(leg.line) : '';
  const overUnder = leg.overUnder || '';
  const verdict = leg.verdict || '';
  const probability = leg.probability != null ? Math.round(leg.probability * 100) : undefined;
  const score = leg.score;

  const legLeagueKey = leagueKeyOrNull(leg.leagueKey);
  const headshotUrl = usePlayerHeadshotUrl(player, legLeagueKey);
  const teamInfo = findTeam(teamAbbr, legLeagueKey);

  return (
    <div className="bg-theme-surface border border-theme-border rounded-xl p-3">
      <div className="flex gap-2.5">
        <AvatarWithBadge headshotUrl={headshotUrl} teamLogo={teamInfo?.logo} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-semibold text-theme-text truncate">{player}</span>
            {verdict && <VerdictBadge verdict={verdict} />}
          </div>
          <div className="flex items-center justify-between gap-2 mt-0.5">
            <span className="text-[10px] text-theme-text-tertiary">
              {teamAbbr && opponentAbbr ? `${teamAbbr} vs ${opponentAbbr}` : teamAbbr}
            </span>
            <span className="text-[10px] text-theme-text-secondary">{formatStatLabel(prop)}</span>
          </div>
          <div className="flex items-center justify-between gap-2 mt-1">
            <span className="text-xs text-theme-text">
              {overUnder} {line}
            </span>
            <div className="flex items-center gap-2 text-xs">
              {score != null && (
                <span className="text-theme-text-secondary">Score: {score}/100</span>
              )}
              {probability != null && (
                <span className={probability >= 50 ? 'text-theme-success' : 'text-theme-danger'}>
                  {probability}%
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
